import { GraphQLError } from 'graphql/index';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';

import useToast from '../../../hooks/useToast';
import { TaskMigrationPairSyncDialog_task$key } from '../../../relay/__generated__/TaskMigrationPairSyncDialog_task.graphql';
import {
  TaskMigrationPairSyncDialog_taskMigrationPairSyncPrepareMutation,
  TaskMigrationPairSyncDialog_taskMigrationPairSyncPrepareMutation$data,
} from '../../../relay/__generated__/TaskMigrationPairSyncDialog_taskMigrationPairSyncPrepareMutation.graphql';
import { parseGraphQLError } from '../../../utils/error';
import Button from '../../core/Button';
import Dialog, { DialogProps } from '../../core/Dialog';
import Stack from '../../core/Stack';
import Text from '../../core/Text';
import TaskLabelingProblemUpdateDialog from '../TaskLabelingProblemUpdateDialog';

const TaskMigrationPairSyncDialog_task = graphql`
  fragment TaskMigrationPairSyncDialog_task on Task {
    id
    isMigrated
    migrationPair {
      id
      sequence
    }
    problem {
      problem
      solution
      creationType
    }
    aiFile {
      key
      size
      objectUrl
    }
  }
`;

type Props = {
  task: TaskMigrationPairSyncDialog_task$key;
  // 문제·해설·정답·변형 정도가 짝과 다른지
  contentChanged: boolean;
  // 이번 출시로 이 문제의 단원·난이도가 바뀌었는지
  labelingChanged: boolean;
} & Pick<DialogProps, 'isOpen' | 'onDismiss'>;

const LABELING_GUIDE = '단원·난이도는 짝 문제의 최종 라벨링에서 직접 판단해 수정해 주세요';

const TaskMigrationPairSyncDialog = ({
  task: taskReference,
  contentChanged,
  labelingChanged,
  isOpen,
  onDismiss,
}: Props) => {
  const { id, isMigrated, migrationPair, problem, aiFile } = useFragment(TaskMigrationPairSyncDialog_task, taskReference);
  const router = useRouter();
  const { toast } = useToast();
  const [pair, setPair] =
    useState<TaskMigrationPairSyncDialog_taskMigrationPairSyncPrepareMutation$data['taskMigrationPairSyncPrepare']>();

  const [prepare, isInFlight] = useMutation<TaskMigrationPairSyncDialog_taskMigrationPairSyncPrepareMutation>(graphql`
    mutation TaskMigrationPairSyncDialog_taskMigrationPairSyncPrepareMutation(
      $input: TaskMigrationPairSyncPrepareInput!
    ) {
      taskMigrationPairSyncPrepare(input: $input) {
        id
        ...TaskLabelingProblemUpdateDialog_task
      }
    }
  `);

  if (!migrationPair || !problem) return null;

  if (pair)
    return (
      <TaskLabelingProblemUpdateDialog
        isOpen={isOpen}
        onDismiss={onDismiss}
        task={pair}
        initialProblem={{
          problem: problem.problem || '',
          solution: problem.solution || '',
          creationType: problem.creationType,
        }}
        initialAiFile={aiFile ? { key: aiFile.key, size: aiFile.size || 0, objectUrl: aiFile.objectUrl } : undefined}
        animation={false}
        config={{
          onCompleted: () => {
            toast('짝 문제에 반영했어요. 검수 후 출시해 주세요', 'success');
            if (labelingChanged) toast(LABELING_GUIDE, 'default');
            onDismiss?.();
            router.push(`/task/${pair.id}`);
          },
        }}
        full
      />
    );

  return (
    <Dialog isOpen={isOpen} onDismiss={onDismiss}>
      <Dialog.Header>
        <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>마이그레이션 짝에도 반영할까요?</Text>
      </Dialog.Header>
      <Dialog.Body>
        <Text as={'p'}>
          {isMigrated ? '마이그레이션 원본' : '마이그레이션본'} {migrationPair.sequence} 문제에도 반영이 필요할 수 있어요.
        </Text>
        {contentChanged ? <Text as={'p'}>· 문제·해설·정답·변형 정도가 짝과 달라요</Text> : null}
        {labelingChanged ? <Text as={'p'}>· 이번 출시로 이 문제의 단원·난이도가 바뀌었어요</Text> : null}
        <Text as={'p'} sx={{ marginTop: 2 }}>
          반영하기를 누르면 짝 문제를 검수 상태로 나에게 배정해요.
          {contentChanged ? ' 이 문제의 내용을 채운 수정하기 창이 열리니 확인 후 저장해 주세요.' : ''}
          {labelingChanged ? ` ${LABELING_GUIDE}.` : ''}
        </Text>
      </Dialog.Body>
      <Dialog.Footer>
        <Stack gapX={2}>
          <Stack.Item>
            <Button variant={'outline'} size={'large'} onClick={() => onDismiss?.()}>
              반영 안 함
            </Button>
          </Stack.Item>
          <Stack.Item>
            <Button
              variant={'primary'}
              size={'large'}
              disabled={isInFlight}
              onClick={() =>
                prepare({
                  variables: { input: { id } },
                  onCompleted: ({ taskMigrationPairSyncPrepare }) => {
                    if (contentChanged) {
                      setPair(taskMigrationPairSyncPrepare);
                      return;
                    }
                    toast(`짝 문제를 검수로 배정했어요. ${LABELING_GUIDE}`, 'success');
                    onDismiss?.();
                    router.push(`/task/${taskMigrationPairSyncPrepare.id}`);
                  },
                  onError: (error) => {
                    toast(
                      parseGraphQLError(error as GraphQLError)?.[0].message ?? '짝 문제를 준비하지 못했어요',
                      'error',
                    );
                  },
                })
              }
            >
              반영하기
            </Button>
          </Stack.Item>
        </Stack>
      </Dialog.Footer>
    </Dialog>
  );
};

export default TaskMigrationPairSyncDialog;
